import { useEffect, useMemo } from 'react'

import Modal from 'react-modal'
import { useStopwatch } from 'react-timer-hook'

import { ResultCode, StepNames } from '@src/enums'
import FeatherOrderSummary from '@src/pages/Exchange/components/FeatherOrderFlow/FeatherOrderSummary'
import FeatherProcessingSwap from '@src/pages/Exchange/components/FeatherOrderFlow/FeatherProcessingSwap'
import FeatherSwapCompleted from '@src/pages/Exchange/components/FeatherOrderFlow/FeatherSwapCompleted'
import FeatherSwapFailed from '@src/pages/Exchange/components/FeatherOrderFlow/FeatherSwapFailed'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useFeatherOrderUpdates } from '@src/pages/Exchange/hooks/useFeatherOrderUpdates'
import { IFee } from '@src/types/fee'
import { createContextLogger } from '@src/utils/logger'
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { useHistoryStore } from '@src/zustand_stores/history.store'

const featherSwapLogger = createContextLogger('FeatherSwapInProgressFlow')

interface IFeatherSwapInProgressFlowProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    currentStep: StepNames
    result?: {
        status: ResultCode
        sourceTxHash?: string
        destinationTxHash?: string
        message?: string
        transactionETA?: number
    }
    fees: IFee[]
    depositAddress?: string
    miles?: number
    orderId?: string
}

const FeatherSwapInProgressFlow: React.FC<IFeatherSwapInProgressFlowProps> = ({
    isOpen,
    onClose,
    onConfirm: parentOnConfirm,
    currentStep = StepNames.UNSTARTED,
    result = undefined,
    fees,
    depositAddress,
    miles,
    orderId,
}: IFeatherSwapInProgressFlowProps) => {
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const { exchangeId } = useApplicationStore()
    const stopwatch = useStopwatch({ autoStart: false })

    // Access exchange data context
    const { sellData, buyData } = useExchangeDataContext()

    // Setup websocket for order updates if we have an order ID
    // Only pass a valid orderId to avoid unnecessary connections
    const validOrderId = orderId || ''
    useFeatherOrderUpdates(exchangeId, validOrderId)

    // Determine the current view based on the step
    const isOrderCompleted = currentStep === StepNames.FEATHER_COMPLETED || result?.status === ResultCode.success
    const isOrderFailed =
        currentStep === StepNames.FEATHER_FAILED ||
        currentStep === StepNames.FEATHER_EXPIRED ||
        result?.status === ResultCode.failure

    // Before setupWebsocket
    useEffect(() => {
        // Log when orderId changes
        console.log('🔥 FeatherSwapInProgressFlow - Order ID changed:', orderId)
        if (orderId) {
            console.log('🔥 WebSocket should be connecting with order ID:', orderId)
        }
    }, [orderId])

    // Wrap the onConfirm in a Promise
    const handleConfirm = () => {
        return new Promise<void>((resolve, reject) => {
            try {
                parentOnConfirm()
                resolve()
            } catch (error: any) {
                handleTransactionError(error)
                reject(error)
            }
        }).catch((error: any) => {
            handleTransactionError(error)
            throw error
        })
    }

    // Handle transaction errors
    const handleTransactionError = (error: any) => {
        featherSwapLogger.error('Transaction error:', { error })
        featherSwapLogger.info('Current step when error occurred:', { currentStep })

        const isUserRejection =
            error?.message?.includes('rejected') ||
            error?.message?.includes('user denied') ||
            error?.message?.includes('user cancelled') ||
            error?.message?.includes('User rejected')

        // Keep track of the current step before it was rejected
        const currentPendingStep = currentStep
        featherSwapLogger.info('Current pending step:', { currentPendingStep })

        // Update the history with appropriate error state
        updateHistory(exchangeId, {
            currentStep: StepNames.FEATHER_FAILED,
            result: {
                status: isUserRejection ? ResultCode.pending : ResultCode.failure,
                message: isUserRejection
                    ? 'Transaction was rejected. Please try again.'
                    : 'Transaction failed. Please try again.',
            },
        })
    }

    // Setup timer for tracking exchange duration
    useEffect(() => {
        if (currentStep === StepNames.FEATHER_DEPOSIT_DETECTED || currentStep === StepNames.FEATHER_PROCESSING) {
            stopwatch.reset()
            stopwatch.start()
        } else if (
            result?.status === ResultCode.success ||
            result?.status === ResultCode.failure ||
            currentStep === StepNames.FEATHER_COMPLETED ||
            currentStep === StepNames.FEATHER_FAILED ||
            currentStep === StepNames.FEATHER_EXPIRED
        ) {
            stopwatch.pause()
        }
    }, [currentStep, result?.status])

    // Determine if we're showing the processing screen or the order summary
    const showProcessingScreen = useMemo(() => {
        // Return true if in an active swap state and not completed/failed
        const shouldShow =
            [
                StepNames.FEATHER_WAITING_FOR_DEPOSIT,
                StepNames.FEATHER_DEPOSIT_DETECTED,
                StepNames.FEATHER_PROCESSING,
                StepNames.FEATHER_SENDING_TO_DESTINATION,
            ].includes(currentStep) &&
            !isOrderCompleted &&
            !isOrderFailed

        console.log('🔄 Calculating showProcessingScreen:', {
            currentStep,
            shouldShow,
            isOrderCompleted,
            isOrderFailed,
            featherStep: currentStep.includes('FEATHER_'),
        })

        return shouldShow
    }, [currentStep, isOrderCompleted, isOrderFailed])

    // Modify the useEffect for step changes
    useEffect(() => {
        console.log('🔄 FeatherSwapInProgressFlow - Current Step:', currentStep)
        console.log('🔄 FeatherSwapInProgressFlow - Show Processing:', showProcessingScreen)
        console.log('🔄 FeatherSwapInProgressFlow - Result:', result)

        // Check if we have a source transaction hash but aren't showing the processing screen
        if (result?.sourceTxHash && !showProcessingScreen && !isOrderCompleted && !isOrderFailed) {
            console.log('🔥 DETECTED TRANSACTION BUT NOT SHOWING PROCESSING SCREEN - FORCING VIEW')
        }

        // Ensure proper step transitions
        if (
            orderId &&
            !showProcessingScreen &&
            !isOrderCompleted &&
            !isOrderFailed &&
            (currentStep === StepNames.FEATHER_WAITING_FOR_DEPOSIT ||
                currentStep === StepNames.FEATHER_DEPOSIT_DETECTED ||
                currentStep === StepNames.FEATHER_PROCESSING)
        ) {
            console.log('🔄 FeatherSwapInProgressFlow - Forcing processing screen to show')
        }
    }, [currentStep, showProcessingScreen, orderId, result, isOrderCompleted, isOrderFailed])

    // Add to useEffect
    useEffect(() => {
        // Immediately check history and force processing view
        const txHash = result?.sourceTxHash
        if (txHash) {
            console.log('⚡ Found transaction hash in FeatherSwapInProgressFlow:', txHash)

            // FORCE STEP CHANGE if we haven't already
            if (currentStep === StepNames.UNSTARTED) {
                console.log('⚡ Forcing step change to FEATHER_WAITING_FOR_DEPOSIT')
                updateHistory(exchangeId, {
                    currentStep: StepNames.FEATHER_WAITING_FOR_DEPOSIT,
                })
            }
        }
    }, [result?.sourceTxHash])

    // Render the appropriate component based on the state
    const renderContent = () => {
        // Show order summary if we have no transaction data or order yet
        if (!result?.sourceTxHash && currentStep === StepNames.UNSTARTED) {
            return (
                <FeatherOrderSummary
                    onClose={onClose}
                    onConfirm={handleConfirm}
                    sellData={sellData}
                    buyData={buyData}
                    fees={fees}
                    depositAddress={depositAddress}
                />
            )
        }

        // Show completed screen
        if (isOrderCompleted) {
            return (
                <FeatherSwapCompleted
                    sellData={sellData}
                    buyData={buyData}
                    fees={fees}
                    orderId={orderId || ''}
                    miles={miles}
                    result={result}
                    fullTradeTimeTimer={stopwatch}
                />
            )
        }

        // Show failed screen
        if (isOrderFailed) {
            return (
                <FeatherSwapFailed
                    sellData={sellData}
                    buyData={buyData}
                    fees={fees}
                    orderId={orderId || ''}
                    currentStep={currentStep}
                    result={result}
                />
            )
        }

        // Show processing screen
        return (
            <FeatherProcessingSwap
                onClose={onClose}
                currentStep={currentStep}
                fees={fees}
                orderId={orderId || ''}
                miles={miles}
                result={result}
                fullTradeTimeTimer={stopwatch}
            />
        )
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            ariaHideApp={false}
            className={`fixed left-1/2 top-1/2 flex max-h-[calc(100vh-90px)] w-[464px] max-w-[calc(100%-24px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden overflow-y-auto outline-none transition-all duration-500`}
            style={{
                overlay: {
                    backgroundColor: 'rgba(0,0,0,.6)',
                },
            }}
            id='feather-swap-progress-modal'
        >
            <div className='space-y-5 rounded-2xl border border-mach-modal-border bg-mach-modal-background px-2 py-7 md:space-y-10'>
                {renderContent()}
            </div>
        </Modal>
    )
}

export default FeatherSwapInProgressFlow
